import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { useState } from "react";
import { ArrowRight, ChevronLeft } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { cn } from "@/shared/lib/cn";
import {
  ACCOUNT_STORAGE_KEY,
  accountRoutes,
  AccountType,
  apiRoleToAccountType,
  getAuthToken,
  getAuthUser,
} from "../auth";

const accountTypes: AccountType[] = ["admin", "auditor", "hospital", "enterprise"];

export function AccountTypeSelectPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<AccountType | "">("");

  const handleContinue = () => {
    if (!selected) return;
    // Si une session API existe déjà, ne pas afficher un autre rôle que celui du token.
    const authUser = getAuthUser();
    if (authUser && getAuthToken()) {
      const realType = apiRoleToAccountType(authUser.role);
      if (realType !== selected) {
        localStorage.setItem(ACCOUNT_STORAGE_KEY, realType);
        navigate(accountRoutes[realType]);
        return;
      }
    }
    localStorage.setItem(ACCOUNT_STORAGE_KEY, selected);
    navigate(accountRoutes[selected]);
  };

  const selectedDescription = selected
    ? t.safecheck.accountType.types[selected].description
    : null;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <div className="flex flex-1 flex-col items-center justify-center px-6 py-12">
        <div className="w-full max-w-md text-center">
          <h1 className="text-2xl font-bold text-foreground">{t.safecheck.accountType.title}</h1>
          <p className="mt-2 text-sm text-muted">{t.safecheck.accountType.subtitle}</p>
        </div>

        <div className="theme-card theme-card-static mt-8 w-full max-w-md p-6 sm:p-7">
          <label
            htmlFor="account-type"
            className="mb-2 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
          >
            {t.safecheck.accountType.accountLabel}
          </label>
          <select
            id="account-type"
            value={selected}
            onChange={(e) => setSelected(e.target.value as AccountType | "")}
            className="h-12 w-full cursor-pointer rounded-xl border border-border bg-card-muted px-4 text-sm text-foreground outline-none transition-colors focus:border-accent focus:ring-2 focus:ring-accent/20"
          >
            <option value="">{t.safecheck.accountType.placeholder}</option>
            {accountTypes.map((id) => (
              <option key={id} value={id}>
                {t.safecheck.accountType.types[id].title}
              </option>
            ))}
          </select>

          {selectedDescription && (
            <p className="mt-4 rounded-xl bg-primary-soft px-4 py-3 text-sm leading-relaxed text-muted-foreground">
              {selectedDescription}
            </p>
          )}

          <button
            type="button"
            onClick={handleContinue}
            disabled={!selected}
            className={cn(
              "mt-6 flex h-12 w-full items-center justify-center gap-2 rounded-xl text-sm font-semibold transition-colors",
              selected
                ? "bg-accent text-accent-foreground hover:bg-accent/90"
                : "cursor-not-allowed bg-muted text-white",
            )}
          >
            {t.safecheck.accountType.continue}
            <ArrowRight size={16} />
          </button>
        </div>

        <Link
          to="/login"
          className="mt-8 flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ChevronLeft size={16} />
          {t.safecheck.accountType.backToLogin}
        </Link>
      </div>
    </div>
  );
}
